import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnInit,
    OnChanges,
    Output,
    SimpleChanges,
    ChangeDetectorRef,
} from '@angular/core';

import * as L from 'leaflet';
import { GeocodingControl } from '@maptiler/geocoding-control/leaflet';

import { latLng } from 'leaflet';

import { Feature, FeatureCollection, ImageInFeature, ImageOnMap } from '../../models';

import { TileProvider, TileService } from '../../services/tile.service';

import { LocationHeatmapColorPipe } from 'shared/pipes/location-heatmap-color/location-heatmap-color.pipe';
import { LocationWithPowerUsageIncluded } from 'fleet/map-page/fleet-map-page.component';
import { Router } from '@angular/router';

@Component({
    selector: 'kp-map',
    templateUrl: './kp-map.component.html',
    styleUrls: ['./kp-map.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KpMapComponent implements OnInit, OnChanges {
    @Input() data: FeatureCollection;
    @Input() tileProvider: TileProvider;
    @Input() mapBoxToken: string;
    @Input() mapTilerToken: string;
    @Input() isEditable = false;
    @Input() defaultLocation: L.LatLngExpression = [50.27836640515094, 8.115810993194996];
    @Input() defaultZoom = 3;
    @Input() locations: LocationWithPowerUsageIncluded[];

    @Output() mutation: EventEmitter<FeatureCollection> = new EventEmitter();
    @Output() removeFeature: EventEmitter<string> = new EventEmitter();
    @Output() editStart: EventEmitter<void> = new EventEmitter();
    @Output() editEnd: EventEmitter<void> = new EventEmitter();
    @Output() uploadImg: EventEmitter<ImageOnMap> = new EventEmitter();
    @Output() setImg: EventEmitter<ImageInFeature> = new EventEmitter();
    @Output() save: EventEmitter<FeatureCollection> = new EventEmitter();
    @Output() cancel: EventEmitter<void> = new EventEmitter();
    @Output() featureClicked = new EventEmitter<Feature>();

    map: L.Map;
    isEditing = false;
    layersControl: L.Control.Layers;
    editingFeature: Feature;
    selectingFeature = false;
    markers: L.FeatureGroup = L.featureGroup();

    private powerUsageList: number[] = [];

    get isFleetTab(): boolean {
        return this.router.url?.includes('fleet');
    }

    constructor(
        private tileService: TileService,
        private cdRef: ChangeDetectorRef,
        private router: Router
    ) {}

    ngOnInit() {
        this.initMap();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.locations) {
            this.createlocationMarkers();
        }
    }

    initMap(): void {
        this.map = L.map('map', {
            zoomControl: false,
            minZoom: 1,
            maxZoom: 22,
            zoomSnap: 1,
            zoomDelta: 1,
            wheelPxPerZoomLevel: 60,
            attributionControl: false,
            worldCopyJump: true,
            center: this.defaultLocation,
            zoom: this.defaultZoom,
        });
        // Layer control
        this.layersControl = L.control.layers().addTo(this.map);
        this.layersControl.setPosition('bottomright');

        // Format tokens from component inputs
        const tokens: Partial<Record<TileProvider, string>> = {};
        if (this.mapBoxToken) {
            tokens.mapBoxStreet = this.mapBoxToken;
            tokens.mapBoxSatellite = this.mapBoxToken;
        }
        if (this.mapTilerToken) {
            tokens.mapTilerStreet = this.mapTilerToken;
            tokens.mapTilerSatellite = this.mapTilerToken;
        }
        // Attempt to load default tile provider
        this.tileProvider !== 'openStreetMap' && tokens[this.tileProvider]
            ? this.tileService.load(this.map, this.layersControl, this.tileProvider, tokens)
            : this.tileService.load(this.map, this.layersControl, 'openStreetMap', tokens);

        this.resetZoom();

        if (!this.isFleetTab && this.mapTilerToken) {
            const geocodingControl = new GeocodingControl({
                apiKey: this.mapTilerToken,
                placeholder: 'Jump to address',
            });
            geocodingControl.options.position = 'topleft';
            geocodingControl.addTo(this.map);
        }
    }

    resetZoom() {
        if (
            this.map &&
            !this.data?.features.every((value) => value === null) &&
            this.data?.features?.filter((f) => f.properties?.located)?.length > 0
        ) {
            const bounds = L.geoJSON(this.data).getBounds();
            if (bounds) {
                this.map.fitBounds(bounds.pad(0.2));
            }
        }
    }

    toggleEditing(forceValue?: boolean) {
        if (!this.isEditable) {
            this.isEditing = false;
            throw new Error('Trying to toggle editing, but the Map is not editable.');
        }
        if (forceValue !== undefined) {
            this.isEditing = forceValue;
        } else {
            this.isEditing = !this.isEditing;
        }
        return this.isEditing ? this.editStart.emit() : this.editEnd.emit();
    }

    // FEATURE SELECT AND PLACE -------------
    selectFeature(): void {
        this.selectingFeature = true;
    }
    closeSelectFeature(): void {
        this.selectingFeature = false;
    }

    /**
     * Place a feature on the map
     * @param feature a Feature object, with geometry.coordinates set to the map coordinates in GeoJSON order
     */
    placeFeature(feature: Feature): void {
        const place = (feature: Feature, offsetX = 0, offsetY = 0) => {
            for (const child of this.data.features) {
                if (child.properties.parent === feature.properties.id) {
                    place(child, offsetX + 1, offsetY + 1.5);
                    offsetY++;
                }
            }
            const { sw, ne } = this.getViewCoords(-0.3);
            // Feature geometry is geoJSON
            feature.geometry.coordinates =
                feature.properties.type === 'image' || feature.properties.type === 'location'
                    ? [
                          [sw.lng, sw.lat + offsetX / 10000],
                          [ne.lng, ne.lat + offsetX / 10000],
                      ]
                    : [ne.lng - offsetY / 22000, sw.lat + offsetX / 10000];
            feature.properties.located = true;
        };
        this.closeSelectFeature();
        place(feature);
        this.mutate(this.data);
    }

    // EDIT FEATURE -------------
    editFeature(feature: Feature) {
        this.editingFeature = feature;
        this.cdRef.markForCheck();
    }

    endEditFeature() {
        this.editingFeature = null;
        this.cdRef.markForCheck();
    }

    // MUTATE -------------
    // A single feature
    mutateFeature(feature: Feature) {
        const features = this.data.features.map((f) => {
            return f.properties.id === feature.properties.id ? (feature as any) : f;
        });
        this.mutation.emit({ ...this.data, ...{ features } });
    }

    // All
    mutate(features: FeatureCollection) {
        this.mutation.emit(features);
    }

    // UP TO THE BACK END to decide how.
    requestRemoveFeature(id: string) {
        this.removeFeature.emit(id);
        this.endEditFeature();
    }

    // Persist -------------
    saveChanges() {
        this.endEditFeature();
        this.save.emit(this.data);
        this.toggleEditing(false);
    }

    cancelChanges() {
        this.endEditFeature();
        this.cancel.emit();
        this.toggleEditing(false);
    }

    // IMAGES (FILE roundtrip) -------------
    async setImage(file: File, feature: Feature) {
        const reader = new FileReader();
        reader.onload = async () => {
            await this.imgToMapCoords(reader.result.toString());
            this.setImg.emit({
                file,
                id: feature.properties.id,
            });
        };

        reader.readAsDataURL(file);
    }

    // COORDINATES -------------
    async addCoordinatesToImage(file: File) {
        const reader = new FileReader();
        reader.onload = async () => {
            const coordinates = await this.imgToMapCoords(reader.result.toString());
            this.uploadImg.emit({
                file,
                coordinates,
            });
        };

        reader.readAsDataURL(file);
    }

    private getViewCoords(pad = -0.15): { sw: L.LatLng; ne: L.LatLng } {
        // Map coords
        const sw = this.map.getBounds().pad(pad).getSouthWest();
        const ne = this.map.getBounds().pad(pad).getNorthEast();
        // ATTN: lat, lng must be in GeoJSON order
        return { sw, ne };
    }

    private async imgToMapCoords(url: string): Promise<[[number, number], [number, number]]> {
        const { sw, ne } = this.getViewCoords();
        // Projected to pixels
        const widthCoordsPerPixels = Math.abs(sw.lng - ne.lng) / this.map.getSize().x;
        const heightCoordsPerPixels = Math.abs(sw.lat - ne.lat) / this.map.getSize().y;

        const img = new Image();
        return new Promise((resolve) => {
            img.onload = () => {
                const isHorizontal = img.width / img.height > this.map.getSize().x / this.map.getSize().y;

                const neWithAspectRatio: L.LatLng = isHorizontal
                    ? L.latLng(
                          sw.lat + (this.map.getSize().x / img.width) * img.height * heightCoordsPerPixels,
                          sw.lng + this.map.getSize().x * widthCoordsPerPixels
                      )
                    : L.latLng(
                          sw.lat + this.map.getSize().y * heightCoordsPerPixels,
                          sw.lng + (this.map.getSize().y / img.height) * img.width * widthCoordsPerPixels
                      );
                // ATTN: lat, lng must be in GeoJSON order
                resolve([
                    [sw.lng, sw.lat],
                    [neWithAspectRatio.lng, neWithAspectRatio.lat],
                ]);
            };
            img.src = url;
        });
    }

    private createlocationMarkers(): void {
        if (this.markers?.getLayers().length) {
            this.markers?.clearLayers();
        }

        if (!this.locations?.length) {
            return;
        }

        const locationsWithLatAndLng = this.locations.filter(
            (location) => location.location?.lat && location.location?.lng
        );
        this.powerUsageList = this.locations.map((location) => location.power);

        if (!locationsWithLatAndLng?.length) {
            console.error('No locations with lat/lng available');
            return;
        }

        const markers = locationsWithLatAndLng.map((location) => {
            return this.createMarker(location);
        });

        this.markers = L.featureGroup(markers, {});
        this.markers.addTo(this.map);

        const latlngs = markers.map((marker) => marker.getLatLng());
        const latlngBounds = L.latLngBounds(latlngs);
        this.map.fitBounds(latlngBounds, { animate: true, duration: 1, maxZoom: 15 });

        this.cdRef.markForCheck();
    }

    private createMarker(item: LocationWithPowerUsageIncluded): L.CircleMarker {
        let min = 0;
        let max = 0;
        let radiusValue = 10;
        let color = 'lightGreen';

        if (this.locations?.length) {
            max = Math.max(...this.powerUsageList);
            min = Math.min(...this.powerUsageList);
            radiusValue = this.calculateMarkerRadius(min, max, item.power) || 10;
            color =
                item.power === 0 ? 'grey' : new LocationHeatmapColorPipe().transform(item.power, this.powerUsageList);
        }

        // for Leaflet, create latLng object from latitude, longitude in that order, not from geoJSON order
        const coordinates = latLng([item.location.lat, item.location.lng]);
        const marker = L.circleMarker(coordinates, {
            className: 'location-marker',
            radius: radiusValue,
            color: color,
            fillColor: color,
            opacity: 0.85,
            fillOpacity: 0.85,
        }).bindTooltip(item.location.name + ` (${item.power} kWh)`, {
            direction: 'top',
            interactive: true,
            offset: [0, -10],
        });
        return marker;
    }

    private calculateMarkerRadius(min: number, max: number, value: number): number {
        // Calculate the range between 10 and 16 (min and max pixel radius for markers)
        const range = 16 - 10;

        const clampedValue = Math.min(Math.max(value, min), max);
        const mappedValue = ((clampedValue - min) / (max - min)) * range + 10;
        const roundedValue = Math.round(mappedValue);

        return roundedValue;
    }
}
